import { useMemo, useState } from "react";
import { FileDown, Archive, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useVault } from "@/context/VaultContext";
import { buildBundle, buildPdf, compileName, gatherNotes, matchNotes } from "@/lib/compile";
import { LEVELS } from "@/lib/watermark";
import { downloadBlob, stamp, ymd } from "@/lib/util";

const today = () => ymd(new Date());
const nice = (d) => new Date(`${d}T00:00`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
const monthStart = () => `${today().slice(0, 8)}01`;

export default function MergeDialog({ open, onOpenChange }) {
  const { notes, folders, profile, folderName } = useVault();
  const [mode, setMode] = useState("range");
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  const [folderId, setFolderId] = useState("all");
  const [ids, setIds] = useState([]);
  const [level, setLevel] = useState("standard");
  const [busy, setBusy] = useState("");
  const [docTitle, setDocTitle] = useState("");
  const [hashes, setHashes] = useState(false);
  const [done, setDone] = useState(null);
  const filter = mode === "range" ? { from, to, folderId } : { ids };
  const matched = useMemo(() => matchNotes(notes, filter), [notes, mode, from, to, folderId, ids]); // eslint-disable-line react-hooks/exhaustive-deps
  const rangeLabel = mode === "range"
    ? `${from ? nice(from) : "Beginning"} to ${to ? nice(to) : "Today"}${folderId !== "all" ? ` · ${folderName(folderId)}` : ""}`
    : `${ids.length} selected entr${ids.length === 1 ? "y" : "ies"}`;

  const run = async (kind) => {
    if (!matched.length) return toast.error("No entries match");
    setBusy(kind);
    try {
      const entries = await gatherNotes(filter);
      const ctx = { profile, level, rangeLabel, includeHashes: hashes, title: docTitle.trim() || "Evidence Package" };
      const name = compileName(ctx, kind);
      if (kind === "pdf") downloadBlob(await buildPdf(entries, ctx), name);
      if (kind === "zip") downloadBlob(await buildBundle(entries, ctx, compileName(ctx, "pdf")), name);
      setDone({ name, count: entries.length, kind });
    } catch (e) {
      toast.error("Compile failed", { description: e.message });
    }
    setBusy("");
  };
  const Btn = ({ kind, icon: Icon, children, variant }) => (
    <Button variant={variant} onClick={() => run(kind)} disabled={!!busy} data-testid={`compile-${kind}-button`}>
      {busy === kind ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Icon className="h-4 w-4 mr-1" />}{children}
    </Button>
  );
  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="merge-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Merge & Compile</DialogTitle>
          <DialogDescription>One continuous chronological document with TOC, full text, watermarked media, version logs and signature certification.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="doc-title">Document Title</Label>
          <Input id="doc-title" value={docTitle} onChange={(e) => setDocTitle(e.target.value)} placeholder="e.g. Receipts & Expense Log, Evidence Package" data-testid="merge-title-input" />
        </div>
        <Tabs value={mode} onValueChange={setMode}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="range" data-testid="merge-tab-range">By Date Range</TabsTrigger>
            <TabsTrigger value="select" data-testid="merge-tab-select">By Selection</TabsTrigger>
          </TabsList>
          <TabsContent value="range" className="grid sm:grid-cols-3 gap-3 pt-4">
            <div className="space-y-1.5"><Label>From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="merge-from-input" /></div>
            <div className="space-y-1.5"><Label>To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="merge-to-input" /></div>
            <div className="space-y-1.5"><Label>Folder</Label>
              <Select value={folderId} onValueChange={setFolderId}>
                <SelectTrigger data-testid="merge-folder-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All folders</SelectItem>{folders.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </TabsContent>
          <TabsContent value="select" className="pt-4 max-h-64 overflow-y-auto space-y-1">
            {[...notes].sort((a, b) => a.createdAt.localeCompare(b.createdAt)).map((n) => (
              <label key={n.id} className="flex items-center gap-3 rounded-md border px-3 py-2 text-sm cursor-pointer hover:bg-muted">
                <Checkbox checked={ids.includes(n.id)} onCheckedChange={(v) => setIds((s) => (v ? [...s, n.id] : s.filter((x) => x !== n.id)))} data-testid={`merge-pick-${n.id}`} />
                <span className="truncate flex-1">{n.title || `${folderName(n.folderId)} - ${stamp(new Date(n.createdAt)).slice(0, 10)}`}</span>
                <span className="font-mono text-[11px] text-muted-foreground hidden sm:inline">{folderName(n.folderId).split(" ")[0]} • {stamp(new Date(n.createdAt)).slice(0, 10)}</span>
              </label>
            ))}
          </TabsContent>
        </Tabs>
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1.5"><Label>Compression</Label>
            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger className="w-48" data-testid="compression-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(LEVELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm pb-2 cursor-pointer">
            <Checkbox checked={hashes} onCheckedChange={(v) => setHashes(!!v)} data-testid="merge-include-hashes-checkbox" />Include technical hash codes
          </label>
          <p className="text-sm pb-2" data-testid="merge-match-count"><span className="font-mono text-primary">{matched.length}</span> matching entries</p>
        </div>
        <div className="flex flex-wrap gap-2 pt-2">
          <Btn kind="pdf" icon={FileDown}>Merged PDF</Btn>
          <Btn kind="zip" icon={Archive} variant="secondary">.ZIP Date-Range Bundle</Btn>
        </div>
        <p className="text-xs text-muted-foreground">ZIP contains the merged PDF + /Evidence_Media/ with every file under its documented name. Raw originals are never compressed.</p>
      </DialogContent>
    </Dialog>
    <DoneAlert done={done} onClose={() => setDone(null)} />
    </>
  );
}

function DoneAlert({ done, onClose }) {
  return (
    <AlertDialog open={!!done} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent data-testid="compile-done-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 font-heading text-xl"><CheckCircle2 className="h-5 w-5" />Compilation Complete!</AlertDialogTitle>
          <AlertDialogDescription data-testid="compile-done-message">
            Your file has been downloaded. Look in your device's Downloads folder{done?.kind === "zip" ? " — open the .ZIP to find the PDF and the Evidence_Media folder" : ""}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {done && <p className="text-xs font-mono break-all rounded-md border p-3" data-testid="compile-done-filename">{done.name}<br /><span className="font-sans text-muted-foreground">{done.count} entr{done.count === 1 ? "y" : "ies"} included</span></p>}
        <AlertDialogFooter><AlertDialogAction onClick={onClose} data-testid="compile-done-ok-button">OK</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
